import contractMoveOutApi from "@/api/contractMoveOutApi";
import { DATETIME_NOT_SECONDS_FORMAT, DATETIME_NOT_SECONDS_FORMAT_JA, DATE_FORMAT_JA } from "@/common/constant";
import { CheckPoint } from "@/store/modules/confirmContract";
import moment from "moment";
import _ from "lodash";
import Company from "./company";
import Contract from "./contract";
import FileModel from "./file";
import Material from "./material";
import Property from "./property";
import User, { USER_ROLE, USER_TYPE } from "./user";
import Repair from "@/model/repair";
import { v4 as uuidv4 } from "uuid";
import FloorArea from "@/model/floorArea";
import MasterDirection from "@/model/masterDirection";
import Face from "@/model/face";
import ContractSignature from "./contractSignature";
import { compareWithKeys, objectKeyModifier } from "@/common/utils";
import trans18next from "@/i18n";
import Location from "./location";
import RepairContent from "./repairContent";

export enum CONTRACT_STEP {
  STEP_ACCEPTED = 0,
  STEP_SCHEDULE = 1,
  STEP_CHECKLIST = 2,
  STEP_SIGNATURE = 3,
  STEP_CORRECT_CHECK_LIST = 4,
  STEP_RE_SIGNATURE = 5,
  STEP_COMPLETE_CHECK_LIST = 6,
  STEP_REQUEST_REPAIR = 7,
  STEP_REVIEW_REQUEST_REPAIR = 8,
  STEP_CREATE_REPORT_REPAIR = 9,
  STEP_COMPLETE_REPORT_REPAIR = 10,
}

export enum CHECKLIST_STATUS {
  DEFAULT = 0,
  NEW = 1,
  UPDATE = 2,
  DELETE = 3,
}
export const CHECKLIST_STATUS_NAME = {
  [CHECKLIST_STATUS.DEFAULT]: '',
  [CHECKLIST_STATUS.NEW]: 'add',
  [CHECKLIST_STATUS.UPDATE]: 'modify',
  [CHECKLIST_STATUS.DELETE]: 'delete',
};

export const ROLE_BY_STEP = {
  [CONTRACT_STEP.STEP_ACCEPTED]: [USER_ROLE.MANAGE], // LV20 StepApplicationAcceptance
  [CONTRACT_STEP.STEP_SCHEDULE]: [USER_ROLE.MANAGE], // LV30 StepSchedule
  [CONTRACT_STEP.STEP_CHECKLIST]: [USER_ROLE.WITNESS, USER_ROLE.MANAGE], // LV40 StepCheckList
  [CONTRACT_STEP.STEP_SIGNATURE]: [USER_ROLE.WITNESS, USER_ROLE.MANAGE], // LV50 StepSignature
  [CONTRACT_STEP.STEP_CORRECT_CHECK_LIST]: [USER_ROLE.OWNER, USER_ROLE.WITNESS, USER_ROLE.MANAGE], // LV60 StepCorrectCheckList
  [CONTRACT_STEP.STEP_RE_SIGNATURE]: [USER_ROLE.MANAGE], // StepReSignature // LV70
  [CONTRACT_STEP.STEP_COMPLETE_CHECK_LIST]: [USER_ROLE.OWNER, USER_ROLE.WITNESS, USER_ROLE.MANAGE], // LV80
  [CONTRACT_STEP.STEP_REQUEST_REPAIR]: [USER_ROLE.MANAGE], // LV90
  [CONTRACT_STEP.STEP_REVIEW_REQUEST_REPAIR]: [USER_ROLE.WITNESS, USER_ROLE.MANAGE], // LV100
  [CONTRACT_STEP.STEP_CREATE_REPORT_REPAIR]: [USER_ROLE.WITNESS, USER_ROLE.MANAGE], // LV110
  [CONTRACT_STEP.STEP_COMPLETE_REPORT_REPAIR]: [USER_ROLE.OWNER, USER_ROLE.WITNESS, USER_ROLE.MANAGE], // LV110
};

export interface IContractMoveOut {
  id: string;
  contract_id: string;
  moving_out_date: string;
  zip_code: string;
  exact_address: string;
  phone_number: string;
  desired_date_1: string;
  desired_date_2: string;
  desired_date_3: string;
  person_in_charge_id: string;
  company_id: string;
  property_id: string;
  desired_date: string;
  step: number;
  contract: Contract;
  property: Property;
}

export class ContractMoveOutDTO implements IContractMoveOut {
  id: string;
  contract_id: string;
  moving_out_date: string;
  zip_code: string;
  location: Location;
  exact_address: string;
  phone_number: string;
  desired_date_1: string;
  desired_date_2: string;
  desired_date_3: string;
  desired_date: string;
  person_in_charge_id: string;
  company_id: string;
  property_id: string;
  contract: Contract;
  property: Property;
  step: number;
  user_created: User;
  manageInCharge: User;
  witness: Company;
  witnessPersonInCharge: User;

  // LV20
  accepted_by: string;
  // LV30
  in_charge_id: string;
  witness_id: string;
  witness_owner_id: string;
  return_item_ids: string[];
  customer_checkout_points: CheckPoint[];
  contract_moving_out_check_list: ContractMoveOutCheckList[] = [];
  number_of_depreciation: number;
  contract_signature: ContractSignature;
  // LV90
  request_repair_witness_id: string;
  request_repair_witness_in_charge_id: string;
  request_repair_manage_in_charge_id: string;
  request_repair_date: string;
  request_repair_witness_in_charge: User;
  request_repair_manage_in_charge: User;
  // LV110
  report_in_charge_id: string;
  report_repair_date: string;
  billing_method: string;
  breakdown_offset: string;
  report_remarks: string;
  repair_content: RepairContent[]
}

export class ContractMoveOutCheckList {
  id: string;
  contract_moving_out_id: string;
  order: number;
  floor_area_id: string;
  floor_area: FloorArea;
  master_direction_id: string;
  face_id: string;
  material_id: string;
  material: Material;
  repair_detail_id: string;
  repair_detail: Repair;
  unit_name_id: string;
  remarks: string;
  unit: number;
  unit_price: number;
  quantity: number;
  ratio_price: number;
  resident_price: number;
  owner_price: number;
  total_price: number;
  image_id: string;
  repair_image_id: FileModel[];
  status: number;
  field_updated: string[];
  report_image_before: FileModel[];
  report_image_after: FileModel[];
  number_of_depreciation: number;
  master_direction: MasterDirection;
  face: Face;
  from_customer_check_list_id: string;
  is_latest: boolean;
  is_show_checkpoint: boolean;
  _id: string;
  svg_position: {
    left: number,
    top: number,
  }

  _objectCache = null;
  _fieldTracking = [
    'floor_area_id',
    'master_direction_id',
    'face_id',
    'material_id',
    'repair_detail_id',
    'remarks',
    'quantity',
    'unit_name_id',
    'unit_price',
    'ratio_price',
    'resident_price',
    'owner_price',
    'total_price',
  ];
  _mappingRelation = {
    floor_area_id: 'floor_area',
    master_direction_id: 'master_direction',
    face_id: 'face',
    material_id: 'material',
    repair_detail_id: 'repair_detail',
    unit_name_id: 'unit',
  }

  constructor(dto?: any) {
    if (dto) {
      Object.assign(this, dto);
      this.ratio_price = _.round(this.ratio_price, 1);
    } else {
      this.new();
    }
    this._objectCache = _.clone(this);
    this._id = this._id ? this._id : uuidv4();
  }
  public new() {
    Object.assign(this, {
      id: null,
      contract_moving_out_id: null,
      order: 1,
      floor_area_id: null,
      master_direction_id: null,
      face_id: null,
      material_id: null,
      material: null,
      repair_detail_id: null,
      unit_name_id: null,
      remarks: null,
      unit: null,
      unit_price: null,
      quantity: null,
      ratio_price: null,
      resident_price: 0,
      owner_price: 0,
      total_price: 0,
      image_id: null,
      repair_image_id: [],
      status: null,
      field_updated: null,
      report_image_before: [],
      report_image_after: [],
      number_of_depreciation: 0,
      is_latest: true,
      is_show_checkpoint: false,
    });
  }

  get getRatioOwner() {
    if (this.ratio_price) {
      return _.round(100 - this.ratio_price, 1);
    }
    return 100;
  }

  public calcRatioPrice(contractMoveOut: ContractMoveOut, numberOfDates) {
    if (this.number_of_depreciation != null && this.number_of_depreciation != 0 && numberOfDates !== 'undefined' && numberOfDates != 0) {
      const ratio = (this.number_of_depreciation / numberOfDates) * 100
      this.ratio_price = _.round(ratio, 1) > 100 ? 100 : _.round(ratio, 1);
      if (this.ratio_price < 0) {
        this.ratio_price = 0;
      }
    } else {
      this.ratio_price = 0;
    }
  }

  public calcRemainDay(moveOut: ContractMoveOut, numberOfDays) {
    if (!moveOut.property) {
      this.number_of_depreciation = 0;
    }
    if (this.material) {

      let lastRepair: any;
      [...moveOut.property.repair_contents, ...moveOut.contract_moving_out_check_list].map((item: any) => {
        if ((item.floor_plan_area_id === this.floor_area_id || item.floor_area_id === this.floor_area_id) &&
          item.face_id === this.face_id &&
          item.direction_id === this.master_direction_id &&
          item.material_id === this.material_id &&
          item.is_latest &&
          !((item.id && item.id === this.id) || (item._id && item._id === this._id))) {
          lastRepair = item;
        }
      });
      if (lastRepair) {
        if (moment(moveOut.moving_out_date).isBefore(lastRepair.repair_date)) {
          this.number_of_depreciation = 0;
          return;
        }
        const numberOfDayLastRepair = moment(moveOut.moving_out_date).diff(lastRepair.repair_date, 'days');
        this.number_of_depreciation = lastRepair.number_of_depreciation - numberOfDayLastRepair;
      } else {
        this.number_of_depreciation = this.material.num_of_days - this.totalConstructionDateByMoveOut(moveOut.property, moveOut.moving_out_date);
      }
      this.number_of_depreciation = this.number_of_depreciation >= 0 ? this.number_of_depreciation : 0;
    }

    if (numberOfDays) {
      this.calcRatioPrice(moveOut, numberOfDays)
    }
  }

  protected totalConstructionDateByMoveOut(property: Property, movingOutDate) {
    if (!property.date_of_construction) {
      return 0;
    }
    const dateConstruction = moment(property.date_of_construction, 'YYYY-M-D', true);

    return moment(movingOutDate).diff(dateConstruction, 'days');
  }

  get getResidentPrice() {
    this.resident_price = this.getTotalPrice - this.getOwnerPrice;
    return this.getTotalPrice - this.getOwnerPrice;
  }

  get getOwnerPrice() {
    const price = this.getTotalPrice - this.getTotalPrice * (this.ratio_price / 100);
    this.owner_price = _.round(price);
    return this.owner_price;
  }

  get getTotalPrice() {
    let total = 0;
    if (this.quantity || this.unit_price) {
      total = _.round(this.quantity * this.unit_price);
    }
    return this.total_price = total;
  }

  get getRepairLocation() {
    // if (this.floor_area && this.master_direction && this.face && this.material) {
    return `${this.floor_area.name}/${this.master_direction.name}/${this.face.name}/${this.material.name}`;
    // }
    // return "";
  }

  get statusName() {
    return CHECKLIST_STATUS_NAME[this.status];
  }

  get isChanged() {
    if (this.id && (this.status == CHECKLIST_STATUS.DEFAULT || this.status == CHECKLIST_STATUS.UPDATE)) {
      const fieldChange = compareWithKeys(this, this._objectCache, this._fieldTracking);
      const imageChang = this.imageChange;
      // image changed
      if (imageChang.isChanged || fieldChange || (this.field_updated && this.field_updated.length > 0)) {
        this.status = CHECKLIST_STATUS.UPDATE;
      } else {
        this.status = CHECKLIST_STATUS.DEFAULT;
      }
      return fieldChange;
    }
    return false;
  }

  get isDeleted() {
    return this.status === CHECKLIST_STATUS.DELETE;
  }

  get imageChange() {
    const imageIds = [];
    const oldImageIds = [];
    this.repair_image_id && this.repair_image_id.map(item => {
      imageIds.push(item.id);
    });
    this._objectCache && this._objectCache.repair_image_id.map(item => {
      oldImageIds.push(item.id);
    });
    const remove = _.difference(oldImageIds, imageIds);
    const add = _.difference(imageIds, oldImageIds);
    return {
      isChanged: remove.length + add.length > 0,
      remove,
      add
    };
  }

  public fieldChange(fieldName: string[]) {
    if (this.status == CHECKLIST_STATUS.UPDATE) {
      let keys = objectKeyModifier(this, this._objectCache, this._fieldTracking);
      if (this.field_updated) {
        keys = [...keys, ...this.field_updated]
      }
      if (this.imageChange.isChanged) {
        keys.push('repair_image_id');
      }
      const intersection = _.intersection(keys, fieldName);
      return intersection.length > 0 ? "on" : "";
    }
    return "";
  }

  get isModifier() {
    return compareWithKeys(this, this._objectCache, this._fieldTracking);
  }

  public changeContent() {
    const result = [];
    // case add new in LV30
    if (!this.id && this.status === CHECKLIST_STATUS.DEFAULT) {
      return result;
    }

    // case add new at LV60 correct check list
    if (!this.id && this.status === CHECKLIST_STATUS.NEW) {
      result.push(`NO.${this.order}を追加しました。`);
      return result;
    }
    const fieldChange = objectKeyModifier(this, this._objectCache, this._fieldTracking);
    if (this.id && this.status === CHECKLIST_STATUS.DELETE && this._objectCache.status !== CHECKLIST_STATUS.DELETE) {
      result.push(`NO.${this.order}を削除しました。`);
      return result;
    }
    if (fieldChange.length > 0) {
      for (const field of fieldChange) {
        let oldValue = this._objectCache[field];
        let newValue = this[field];
        if (this._mappingRelation[field]) {
          oldValue = this._objectCache[this._mappingRelation[field]].name;
          newValue = this[this._mappingRelation[field]].name;
        }
        result.push(`NO.${this.order}の${trans18next.t('contractMoveOutFieldName.' + field).toString()}が${oldValue}→${newValue}に変更されました。`);
      }
    }
    if (this.imageChange.isChanged) {
      result.push(`NO.${this.order}の画像変更`);
    }

    return result;
  }
}

export default class ContractMoveOut extends ContractMoveOutDTO {
  message = null
  is_resolved = false;
  re_sign = false;
  is_changed = false;
  is_send_mail = true;
  constructor(dto?: ContractMoveOutDTO) {
    super();
    if (dto) {
      Object.assign(this, dto);
      dto.contract && (this.contract = new Contract(dto.contract));
      dto.property && (this.property = new Property(dto.property));
      if (dto.contract_moving_out_check_list && dto.contract_moving_out_check_list.length > 0) {
        this.contract_moving_out_check_list = dto.contract_moving_out_check_list.map(item => new ContractMoveOutCheckList(item));
      }
      dto.contract_signature && (this.contract_signature = new ContractSignature(dto.contract_signature));
      dto.repair_content && (this.repair_content = dto.repair_content.map(item => new RepairContent(item)))
    } else {
      this.new();
    }
  }

  public new() {
    Object.assign(this, {
      id: null,
      contract_id: null,
      moving_out_date: null,
      zip_code: null,
      exact_address: null,
      phone_number: null,
      desired_date_1: null,
      desired_date_2: null,
      desired_date_3: null,
      person_in_charge_id: null,
      company_id: null,
      property_id: null,
    });
  }

  public addNewCheckList(status = CHECKLIST_STATUS.DEFAULT): ContractMoveOutCheckList {
    const newCheckList = new ContractMoveOutCheckList();
    newCheckList.order = this.contract_moving_out_check_list ? this.contract_moving_out_check_list.length + 1 : 1;
    // newCheckList._id = uuidv4();
    newCheckList.status = status;
    this.contract_moving_out_check_list.push(newCheckList);
    return newCheckList;
  }

  public removeCheckPoint(checkPoint: ContractMoveOutCheckList) {
    const index = this.contract_moving_out_check_list.findIndex(item => (item.id && item.id === checkPoint.id) || item._id === checkPoint._id);
    if (index > -1) {
      this.contract_moving_out_check_list.splice(index, 1);
    }
    // re-index
    let order = 1;
    this.contract_moving_out_check_list.map(item => {
      item.order = order++;
      return item;
    })
  }

  public getCheckListChange() {
    let result = [];
    for (const checkList of this.contract_moving_out_check_list) {
      result = [...result, ...checkList.changeContent()];
    }

    return result;
  }

  public save() {
    if (this.id) {
      // return this.update();
    } else {
      return this.addNew();
    }
  }

  public async accept() {
    return await contractMoveOutApi.accept(this);
  }

  public async updateMoveOutDate() {
    return await contractMoveOutApi.updateMoveOutDate(this);
  }

  public async reject() {
    return await contractMoveOutApi.reject(this);
  }

  public async schedule() {
    return await contractMoveOutApi.schedule(this);
  }

  public async goBack(data) {
    return await contractMoveOutApi.goBack(this, data);
  }

  public async submitCheckList() {
    return await contractMoveOutApi.checkList(this);
  }

  public async signature() {
    return await contractMoveOutApi.signature(this);
  }
  public async reSignature() {
    return await contractMoveOutApi.reSignature(this);
  }

  public async correctCheckList() {
    return await contractMoveOutApi.correctCheckList(this);
  }

  public async repairRequest() {
    return await contractMoveOutApi.repairRequest(this);
  }

  public async repairRequestProcess() {
    return await contractMoveOutApi.repairRequestProcess(this);
  }

  public async approvalRepairRequest() {
    return await contractMoveOutApi.approvalRepairRequest(this);
  }

  public async updateReport() {
    return await contractMoveOutApi.updateReport(this);
  }

  protected async addNew() {
    const dataTranform = Object.assign({}, this);

    return await contractMoveOutApi.store(dataTranform);
  }


  public totalContructionDayFormat(moveIn) {
    if (!this.moving_out_date) {
      return "";
    }
    const totalDays = moment(this.moving_out_date).diff(moveIn, 'days');;
    const years = Math.floor(totalDays / 365);
    const months = Math.floor((totalDays % 365) / 30);
    const days = Math.floor((totalDays % 365) % 30);

    return `${years}年${months}ヶ月${days}日（${totalDays}日）`;
  }

  get getDesiredDateFormat() {
    return this.desired_date ? moment(this.desired_date).format(DATETIME_NOT_SECONDS_FORMAT) : "";
  }

  get getDesiredDateFormatJa() {
    return this.desired_date ? moment(this.desired_date).format(DATETIME_NOT_SECONDS_FORMAT_JA) : "";
  }

  get getDesiredDate1Format() {
    return this.desired_date_1 ? moment(this.desired_date_1).format(DATETIME_NOT_SECONDS_FORMAT_JA) : "";
  }

  get getDesiredDate2Format() {
    return this.desired_date_2 ? moment(this.desired_date_2).format(DATETIME_NOT_SECONDS_FORMAT_JA) : "";
  }

  get getDesiredDate3Format() {
    return this.desired_date_3 ? moment(this.desired_date_3).format(DATETIME_NOT_SECONDS_FORMAT_JA) : "";
  }

  get movingOutDateFormat() {
    return moment(this.moving_out_date).format(DATE_FORMAT_JA);
  }

  get getResidentPrice() {
    return this.getTotalPrice - this.getOwnerPrice;
  }

  get getOwnerPrice() {
    let total = 0;
    this.contract_moving_out_check_list.map(item => {
      !item.isDeleted && (total += item.owner_price);
    });
    return total;
  }

  get getTotalPrice() {
    let total = 0;
    this.contract_moving_out_check_list.map(item => {
      !item.isDeleted && (total += item.total_price);
    });
    return total;
  }

  get getRequestRepairDate() {
    return this.request_repair_date ? moment(this.request_repair_date).format(DATE_FORMAT_JA) : "";
  }

  get createByStaff(): boolean {
    if (this.user_created) {
      return this.user_created.type === USER_TYPE.STAFF;
    }
    return false;
  }

  get contractorName() {
    if (!this.contract) {
      return trans18next.t('data_was_transfered').toString();
    }
    return this.contract.contractor_name
  }

  get propertyName() {
    if (!this.property) {
      return trans18next.t('data_was_transfered').toString();
    }
    return this.property.shortName
  }

  get isTranfered() {
    return !this.property || !this.contract;
  }
}
